import type { Chart as ChartJS, Plugin } from 'chart.js';

/**
 * Charts whose first `update()` has completed. A `WeakSet` rather than a field
 * on the chart: nothing outside this module should be able to observe or set
 * it, and a destroyed chart drops out with no teardown hook to forget.
 */
const updatedCharts = new WeakSet<ChartJS>();

/**
 * Restores Chart.js' own invariant that **a chart is never drawn before its
 * first `update()`**, which Chart.js itself breaks whenever `resizeDelay` is
 * non-zero.
 *
 * ---
 *
 * ## The defect this exists for (ENG-3184)
 *
 * `chartjs-plugin-datalabels` creates the array it draws from in `beforeUpdate`
 * and fills it in `afterUpdate`:
 *
 * ```js
 * beforeInit:  chart.$datalabels = { _actives: [] };   // no _labels yet
 * beforeUpdate: expando._labels = [];
 * afterUpdate:  expando._labels = layout.prepare(expando._datasets);
 * afterDatasetsDraw: layout.draw(chart, expando._labels);  // reads .length
 * ```
 *
 * So a draw that happens after `beforeInit` but before the first `beforeUpdate`
 * reaches `layout.draw(chart, undefined)` and throws
 * `TypeError: Cannot read properties of undefined (reading 'length')` at
 * `Object.draw` / `Object.afterDatasetsDraw`. That is the production stack on
 * `/dashboard/screening-analytics/profiles`.
 *
 * ## How Chart.js gets there
 *
 * `Chart`'s constructor builds
 * `this._doResize = debounce((mode) => this.update(mode), options.resizeDelay || 0)`,
 * and `debounce` **returns the delay**, not whether it ran anything. `_resize()`
 * treats that return value as "a resize happened, repaint":
 *
 * ```js
 * if (this.attached) { if (this._doResize(mode)) { this.render(); } }
 * ```
 *
 * With the default `resizeDelay: 0` the debounce runs `update()` synchronously
 * and returns `0`, so `render()` is never reached from here — the update does
 * its own render, in the right order. With ENG-3130's `resizeDelay: 250` the
 * update is deferred and the truthy delay makes `_resize()` call `render()`
 * **immediately**, 250ms before the update it is supposed to be painting.
 *
 * `_initialize()` reaches that call inside the constructor itself:
 * `bindEvents()` -> `bindResponsiveEvents()` -> `attached()` sets
 * `this.attached = true` and calls `resize()` a second time, all *before* the
 * constructor's own `if (this.attached) this.update()`. The second `_resize()`
 * normally short-circuits, because `retinaScale()` returns false when nothing
 * changed — and that is the last piece:
 *
 * ```js
 * const deviceHeight = round1(chart.height * pixelRatio);
 * const deviceWidth = round1(chart.width * pixelRatio);   // may be fractional
 * …
 * if (chart.currentDevicePixelRatio !== pixelRatio
 *     || canvas.height !== deviceHeight
 *     || canvas.width !== deviceWidth) {
 *   canvas.width = deviceWidth;                           // unsigned long: TRUNCATES
 *   …
 *   return true;
 * }
 * ```
 *
 * `canvas.width` is an integer attribute, so a fractional device width can
 * never be stored. The truncating store one call makes is what the *next*
 * call's comparison observes, so from the first fractional measurement onward
 * the check is true on every call. A plot box whose CSS width is not a whole
 * number of device pixels therefore makes `retinaScale()` report a change
 * forever, the second `_resize()` runs to completion, and the chart renders
 * before it has ever updated.
 *
 * Measured in a production build of this app at DPR 1: the two charts sit in a
 * `grid-cols-2` pair, so every **odd** viewport width halves to a `.5` plot box
 * and both widgets die, while every even width is clean — 1280/1440/1512/1920
 * render, 1281/1441/1513/1921 show `We could not load this widget.` twice.
 *
 * ## Why this and not the alternatives
 *
 * A `try`/`catch` around the plugin, or dropping `datalabels`, would delete the
 * D67 direct labels the plugin is registered for. Lowering `resizeDelay` would
 * undo ENG-3130's measured 22-updates-to-1 reflow saving and only narrows the
 * window rather than closing it. Rounding the plot box is not something a
 * component can promise: the width comes from a `1fr` grid track.
 *
 * Cancelling the render is exact, because the premature render has nothing to
 * paint: it is issued from inside `_initialize()`, and the constructor calls
 * `update()` — which renders properly — three statements later. Nothing is
 * deferred and no frame is lost. In the rarer case of a chart constructed while
 * detached (Chart.js skips the constructor's `update()` entirely), the render
 * is deferred to the debounced `update()` instead of throwing.
 *
 * `beforeRender` rather than `beforeDraw`, and it is a complete gate even
 * though `Animator._update` calls `chart.draw()` directly with no `render()`
 * frame on the stack. The animator loop skips any chart whose `anims.running`
 * is false; `anims.running` is set only by `Animator.start()`; and `start()`
 * has exactly one caller, `render()`, past this hook. So an unrendered chart
 * is never in the animator's running set, and guarding `render()` costs one
 * `WeakSet` lookup per render rather than one per animation frame.
 */
export const renderAfterFirstUpdatePlugin: Plugin = {
  id: 'minervaRenderAfterFirstUpdate',

  afterUpdate(chart) {
    updatedCharts.add(chart);
  },

  /**
   * Only an explicit `false` cancels a Chart.js cancellable hook, so this
   * returns the boolean directly: `true` once the chart has updated, `false`
   * while it has not.
   */
  beforeRender(chart) {
    return updatedCharts.has(chart);
  },
};
